import { DISC_FORMATS, type DiscFormat, type WishlistItem } from './types';
import { normalize } from './search';
import type { Locale } from './i18n/index';

/**
 * The wishlist: things you do not own yet, and the ones you have thrown away
 * recently enough to change your mind about.
 */

/** How long a deleted entry stays recoverable. */
export const TRASH_RETENTION_DAYS = 30;

const MS_PER_DAY = 86_400_000;

/**
 * Fill in the fields a document written by an older version of the app does
 * not have.
 *
 * Entries predating the buyable flag were all things that could be bought —
 * that is why they were on the list — so that is the safer default. Reading
 * through this keeps every screen free of `?? []` and `?? true`.
 */
export function normalizeWishlistItem(raw: WishlistItem): WishlistItem {
  return {
    ...raw,
    genreIds: Array.isArray(raw.genreIds) ? raw.genreIds : [],
    buyable: typeof raw.buyable === 'boolean' ? raw.buyable : true,
    deletedAt: raw.deletedAt ?? null,
  };
}

export function isInTrash(item: WishlistItem): boolean {
  return Boolean(item.deletedAt);
}

/** Days left before an entry in the trash is purged. Zero once it is due. */
export function daysLeftInTrash(item: WishlistItem, now: Date = new Date()): number {
  if (!item.deletedAt) return TRASH_RETENTION_DAYS;
  const deleted = new Date(item.deletedAt).getTime();
  if (Number.isNaN(deleted)) return 0;
  const elapsed = Math.floor((now.getTime() - deleted) / MS_PER_DAY);
  return Math.max(0, TRASH_RETENTION_DAYS - elapsed);
}

/** Entries whose retention has run out and which should now be deleted for good. */
export function expiredFromTrash(items: WishlistItem[], now: Date = new Date()): WishlistItem[] {
  return items.filter((item) => isInTrash(item) && daysLeftInTrash(item, now) === 0);
}

// --- Filtering ---------------------------------------------------------------

/** Only the sorts that mean something for a list of things you do not own. */
export type WishlistSortKey = 'title' | 'added' | 'releaseDate' | 'runtime';
export type BuyableState = 'all' | 'buyable' | 'upcoming';

export interface WishlistFilter {
  query: string;
  /** OR-ed together, matched against the format you are holding out for. */
  formats: DiscFormat[];
  genreIds: number[];
  buyable: BuyableState;
  sort: WishlistSortKey;
  direction: 'asc' | 'desc';
}

export const WISHLIST_SORT_KEYS: readonly WishlistSortKey[] = [
  'title',
  'added',
  'releaseDate',
  'runtime',
] as const;
export const BUYABLE_STATES: readonly BuyableState[] = ['all', 'buyable', 'upcoming'] as const;

export const DEFAULT_WISHLIST_FILTER: WishlistFilter = {
  query: '',
  formats: [],
  genreIds: [],
  buyable: 'all',
  sort: 'added',
  direction: 'desc',
};

function wishlistHaystack(item: WishlistItem): string {
  const parts = [item.title, item.titleDe, item.note, item.year ? String(item.year) : null];
  return normalize(parts.filter(Boolean).join(' '));
}

export function wishlistDisplayTitle(item: WishlistItem, locale: Locale): string {
  if (locale === 'de' && item.titleDe) return item.titleDe;
  return item.title;
}

export function filterWishlist(items: WishlistItem[], filter: WishlistFilter): WishlistItem[] {
  const tokens = normalize(filter.query).split(' ').filter(Boolean);

  return items.filter((item) => {
    if (filter.formats.length > 0) {
      // An entry with no format in mind matches no format filter.
      if (!item.desiredFormat || !filter.formats.includes(item.desiredFormat)) return false;
    }
    if (filter.buyable === 'buyable' && !item.buyable) return false;
    if (filter.buyable === 'upcoming' && item.buyable) return false;
    if (filter.genreIds.length > 0) {
      if (!filter.genreIds.some((id) => item.genreIds.includes(id))) return false;
    }
    if (tokens.length > 0) {
      const target = wishlistHaystack(item);
      if (!tokens.every((token) => target.includes(token))) return false;
    }
    return true;
  });
}

export function sortWishlist(
  items: WishlistItem[],
  sort: WishlistSortKey,
  direction: 'asc' | 'desc',
  locale: Locale,
): WishlistItem[] {
  const collator = new Intl.Collator(locale, { sensitivity: 'base', numeric: true });
  const sign = direction === 'asc' ? 1 : -1;
  const byTitle = (a: WishlistItem, b: WishlistItem) =>
    collator.compare(wishlistDisplayTitle(a, locale), wishlistDisplayTitle(b, locale));

  return [...items].sort((a, b) => {
    let result = 0;
    switch (sort) {
      case 'title':
        return sign * byTitle(a, b);
      case 'added':
        result = a.addedAt.localeCompare(b.addedAt);
        break;
      case 'releaseDate': {
        const left = a.releaseDate ?? (a.year ? `${a.year}-00-00` : null);
        const right = b.releaseDate ?? (b.year ? `${b.year}-00-00` : null);
        if (left === null && right === null) result = 0;
        else if (left === null) return 1;
        else if (right === null) return -1;
        else result = left.localeCompare(right);
        break;
      }
      case 'runtime': {
        const left = a.runtime;
        const right = b.runtime;
        // Unknowns sink to the bottom whichever way the sort runs.
        if (left === null && right === null) result = 0;
        else if (left === null) return 1;
        else if (right === null) return -1;
        else result = left - right;
        break;
      }
    }
    if (result === 0) return byTitle(a, b);
    return sign * result;
  });
}

export function filterAndSortWishlist(
  items: WishlistItem[],
  filter: WishlistFilter,
  locale: Locale,
): WishlistItem[] {
  return sortWishlist(filterWishlist(items, filter), filter.sort, filter.direction, locale);
}

export function activeWishlistFilterCount(filter: WishlistFilter): number {
  let count = 0;
  if (filter.formats.length > 0) count += 1;
  if (filter.genreIds.length > 0) count += 1;
  if (filter.buyable !== 'all') count += 1;
  return count;
}

/** Same contract as `parseFilter`: per-field fallback, never throws. */
export function parseWishlistFilter(value: unknown): WishlistFilter {
  if (typeof value !== 'object' || value === null) return DEFAULT_WISHLIST_FILTER;
  const raw = value as Record<string, unknown>;

  const oneOf = <T extends string>(input: unknown, allowed: readonly T[], fallback: T): T =>
    typeof input === 'string' && (allowed as readonly string[]).includes(input)
      ? (input as T)
      : fallback;

  const formats = Array.isArray(raw.formats)
    ? raw.formats.filter((entry): entry is DiscFormat =>
        (DISC_FORMATS as readonly unknown[]).includes(entry),
      )
    : DEFAULT_WISHLIST_FILTER.formats;

  const genreIds = Array.isArray(raw.genreIds)
    ? raw.genreIds.filter(
        (entry): entry is number => typeof entry === 'number' && Number.isFinite(entry),
      )
    : DEFAULT_WISHLIST_FILTER.genreIds;

  return {
    query: typeof raw.query === 'string' ? raw.query : DEFAULT_WISHLIST_FILTER.query,
    formats: [...new Set(formats)],
    genreIds: [...new Set(genreIds)],
    buyable: oneOf<BuyableState>(raw.buyable, BUYABLE_STATES, DEFAULT_WISHLIST_FILTER.buyable),
    // 'year' was the old name for this, and may still be sitting in a tab's
    // session storage.
    sort:
      raw.sort === 'year'
        ? 'releaseDate'
        : oneOf<WishlistSortKey>(raw.sort, WISHLIST_SORT_KEYS, DEFAULT_WISHLIST_FILTER.sort),
    direction: oneOf<'asc' | 'desc'>(
      raw.direction,
      ['asc', 'desc'] as const,
      DEFAULT_WISHLIST_FILTER.direction,
    ),
  };
}
